import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import * as WebBrowser from 'expo-web-browser';
import {
  Building2,
  Calendar,
  ChevronLeft,
  ExternalLink,
  Hammer,
  Home as HomeIcon,
  Phone,
  Search as SearchIcon,
  ShieldCheck,
  Siren,
  Sparkles,
  Users,
  X,
} from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  PRO_TRADES,
  buildProviderUrl,
  listChainProviders,
  listEmergencyProviders,
  listFeaturedProviders,
  listSearchProviders,
  providerSupportsTrade,
  type ProProvider,
  type ProviderTier,
} from '@/data/pro-directories';
import type { ContactRow } from '@/db/types';
import { listContacts } from '@/lib/contacts';
import { webTransition } from '@/lib/hover-style';
import { goBackOrTo, goTo } from '@/lib/nav';
import { webNoOutline } from '@/lib/web-style';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Find-a-Pro page.
 *
 * Single source of truth: `activeTrade` (a free-text string). It's set
 * by EITHER the trade chips OR the search input — whichever was last
 * interacted with. The directory cards' URLs pre-fill with that value.
 *
 * Why one state, not two: the previous design coupled search to chip
 * filtering, which made search feel like "type to find the right chip"
 * rather than "search for whatever you want." Users wanted to type
 * "foundation crack repair specialist near a flooded basement" or
 * similar and have it just work — that doesn't match a chip and
 * shouldn't have to.
 *
 * Two-column desktop layout: at wide viewports we render the
 * directory cards in a 2-col grid so the page doesn't feel
 * vertically endless. Mobile stays single-column.
 */

const POPULAR_KEYS = ['plumber', 'electrician', 'hvac technician', 'roofer', 'handyman'];

type CategoryTab = 'popular' | 'emergency' | 'home' | 'outside' | 'specialty' | 'all';

const CATEGORY_TABS: { key: CategoryTab; label: string; Icon?: typeof Siren }[] = [
  { key: 'popular', label: 'Popular' },
  { key: 'emergency', label: 'Emergency', Icon: Siren },
  { key: 'home', label: 'In the home' },
  { key: 'outside', label: 'Yard & exterior' },
  { key: 'specialty', label: 'Specialty' },
  { key: 'all', label: 'All' },
];

const CATEGORY_MAP: Record<CategoryTab, Set<string>> = {
  popular: new Set(POPULAR_KEYS),
  emergency: new Set([
    'plumber', 'water damage restoration', 'mold remediation',
    'septic service', 'electrician', 'hvac technician', 'locksmith',
    'roofer',
  ]),
  all: new Set(PRO_TRADES.map((t) => t.key)),
  home: new Set([
    'plumber', 'electrician', 'hvac technician', 'handyman', 'house cleaner',
    'appliance repair', 'water heater installer', 'locksmith', 'home inspector',
  ]),
  outside: new Set([
    'roofer', 'landscaper', 'arborist tree service', 'pest control',
    'septic service', 'gutter cleaning', 'fence installer', 'deck builder',
    'pressure washing', 'lawn care', 'snow removal', 'pool service',
    'window cleaner',
  ]),
  specialty: new Set([
    'chimney sweep', 'painter', 'generator service', 'masonry chimney repair',
    'foundation specialist', 'water damage restoration', 'mold remediation',
    'solar installer', 'EV charger installer', 'garage door repair',
  ]),
};

// Desktop two-column kicks in above this width. Matches the column
// shell's breakpoint elsewhere so the cards naturally fit two-across.
const DESKTOP_BREAKPOINT = 760;

// Initial state: nothing in the search box. Pre-filling with "Plumber"
// confused users into thinking the page was already filtered to plumbing.
// Empty start = clean canvas; the directory cards still work via the
// empty-state fallback below.
const DEFAULT_TRADE = '';

// What the directory link-outs should search for when the user hasn't
// typed anything yet. We can't pass an empty string to Yelp/Angi/etc.
// because that lands on a generic page with no results. "home repair"
// is broad enough to return useful local options for every trade and
// is the most common search-engine match across the major directories
// (better than "home service pros" which Yelp specifically interprets
// as a narrow category).
const EMPTY_TRADE_FALLBACK = 'home repair';

export default function FindAPro() {
  const router = useRouter();
  const db = useSQLiteContext();
  const { width: viewportWidth } = useWindowDimensions();
  const twoCol = viewportWidth >= DESKTOP_BREAKPOINT;

  const [zip, setZip] = useState<string>('');
  // Single source of truth — set by chips OR search. Last interaction wins.
  const [activeTrade, setActiveTrade] = useState<string>(DEFAULT_TRADE);
  const [category, setCategory] = useState<CategoryTab>('popular');
  const [contacts, setContacts] = useState<ContactRow[]>([]);

  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      if (prop?.zip) setZip(prop.zip);
      if (prop) setContacts(await listContacts(db, prop.id));
    })();
  }, [db]);

  // Saved contacts matching the active trade. Stem-matching (first 4
  // letters of each meaningful search token) lets "roofer" hit
  // "Roofing", "plumber" hit "Plumbing", "electrician" hit "Electric".
  const matchingContacts = useMemo(() => {
    const q = activeTrade.trim().toLowerCase();
    if (!q) return [];
    const stems = q
      .split(/[\s/]+/)
      .filter((w) => w.length >= 4 || w === 'hvac')
      .map((w) => (w === 'hvac' ? 'hvac' : w.slice(0, 4)));
    if (stems.length === 0) return [];
    return contacts
      .filter((c) => {
        const hay = `${c.role ?? ''} ${c.company ?? ''} ${c.name}`.toLowerCase();
        return stems.some((s) => hay.includes(s));
      })
      .slice(0, 4);
  }, [contacts, activeTrade]);

  // Determine which chip (if any) corresponds to the active trade.
  // Match by exact label OR key (case-insensitive). If the user typed
  // free-form text in the search box, no chip is highlighted.
  const matchingChipKey = useMemo(() => {
    const t = activeTrade.trim().toLowerCase();
    if (!t) return null;
    return (
      PRO_TRADES.find(
        (c) => c.key.toLowerCase() === t || c.label.toLowerCase() === t
      )?.key ?? null
    );
  }, [activeTrade]);

  const visibleTrades = useMemo(() => {
    if (category === 'emergency') {
      // The emergency category is a curated subset, sorted in the
      // order most people reach for at 2am.
      return PRO_TRADES.filter((t) => CATEGORY_MAP.emergency.has(t.key));
    }
    return PRO_TRADES.filter((t) => CATEGORY_MAP[category].has(t.key));
  }, [category]);

  const openProvider = async (p: ProProvider) => {
    // Use the fallback when the search box is empty so the link-out
    // doesn't land on a directory's bare homepage with no query.
    const trade = activeTrade.trim() || EMPTY_TRADE_FALLBACK;
    const url = buildProviderUrl(p, trade, zip || null);
    try {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        await WebBrowser.openBrowserAsync(url, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
        });
      }
    } catch (err) {
      try {
        await Linking.openURL(url);
      } catch (err2) {
        console.warn('Failed to open provider', err2);
      }
    }
  };

  const featured = listFeaturedProviders();
  const searchProviders = listSearchProviders();
  const chainProviders = listChainProviders();
  const emergencyProviders = listEmergencyProviders();
  const isEmergencyMode = category === 'emergency';

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <View style={styles.topbarLeft}>
          <Pressable
            onPress={() => goBackOrTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Home"
            onPress={() => goTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <HomeIcon size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Schedule"
            onPress={() => goTo(router, '/(tabs)/schedule')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <Calendar size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
        </View>
        <Text style={styles.topbarTitle}>Find a Pro</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.h1}>Get the right pro, fast.</Text>
          <Text style={styles.lead}>
            We open established directories with your search pre-filled. Pick
            a trade chip or type whatever you need — the cards below pre-fill
            with whichever you used last.
          </Text>
        </View>

        {/* Search + ZIP input. Either source mutates activeTrade — the
            chip selection visually de-highlights when the user starts
            typing something that doesn't match a chip. */}
        <View style={styles.searchCard}>
          <View style={styles.searchRow}>
            <SearchIcon size={16} color={Palette.forest} strokeWidth={2} />
            <TextInput
              value={activeTrade}
              onChangeText={(v) => setActiveTrade(v)}
              placeholder="Plumber, foundation crack, EV charger…"
              placeholderTextColor={Palette.inkSoft}
              style={[styles.searchInput, webNoOutline]}
            />
            {activeTrade.length > 0 ? (
              <Pressable
                onPress={() => setActiveTrade('')}
                hitSlop={8}
                accessibilityLabel="Clear search">
                <X size={14} color={Palette.inkFaint} strokeWidth={2} />
              </Pressable>
            ) : null}
            <View style={styles.zipBox}>
              <Text style={styles.zipBoxLabel}>ZIP</Text>
              <TextInput
                value={zip}
                onChangeText={(v) => setZip(v.replace(/[^0-9]/g, '').slice(0, 5))}
                placeholder="48507"
                placeholderTextColor={Palette.inkSoft}
                inputMode="numeric"
                maxLength={5}
                style={[styles.zipInput, webNoOutline]}
              />
            </View>
          </View>
        </View>

        {/* Active query banner — shows what's about to be sent to the
            directories. Distinct visual when in emergency mode so the
            user knows they're in a different surface area. */}
        <View
          style={[
            styles.queryBanner,
            isEmergencyMode && styles.queryBannerEmergency,
          ]}>
          {isEmergencyMode ? (
            <Siren size={16} color={Palette.cream} strokeWidth={2} />
          ) : (
            <Hammer size={16} color={Palette.cream} strokeWidth={1.7} />
          )}
          <Text style={styles.queryBannerText} numberOfLines={2}>
            {activeTrade.trim() ? (
              <>
                Looking for{' '}
                <Text style={styles.queryBannerEm}>
                  {activeTrade.trim()}
                </Text>
                {zip ? (
                  <Text style={styles.queryBannerSub}> near {zip}</Text>
                ) : (
                  <Text style={styles.queryBannerSub}> — add a ZIP for local results</Text>
                )}
              </>
            ) : (
              <Text style={styles.queryBannerSub}>
                Pick a trade or type one to search.
              </Text>
            )}
          </Text>
        </View>

        {/* Category tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsRow}>
          {CATEGORY_TABS.map((t) => {
            const sel = category === t.key;
            const isEmerg = t.key === 'emergency';
            return (
              <Pressable
                key={t.key}
                onPress={() => setCategory(t.key)}
                style={({ pressed }) => [
                  styles.tab,
                  sel && styles.tabSel,
                  sel && isEmerg && styles.tabSelEmergency,
                  pressed && { opacity: 0.9 },
                ]}>
                {t.Icon ? (
                  <t.Icon
                    size={12}
                    color={sel ? Palette.cream : Palette.clay}
                    strokeWidth={2}
                  />
                ) : null}
                <Text style={[styles.tabText, sel && styles.tabTextSel]}>
                  {t.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* Trade chips */}
        <View style={styles.tradeGrid}>
          {visibleTrades.map((t) => {
            const sel = t.key === matchingChipKey;
            return (
              <Pressable
                key={t.key}
                onPress={() => setActiveTrade(t.label)}
                style={({ pressed }) => [
                  styles.tradeChip,
                  sel && styles.tradeChipSel,
                  pressed && { opacity: 0.9 },
                ]}>
                <Text
                  style={[
                    styles.tradeChipText,
                    sel && styles.tradeChipTextSel,
                  ]}>
                  {t.label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* Emergency tier — only when the emergency category is active.
            Floats above the regular sections so a panicked user has the
            shortest path to a phone number. */}
        {isEmergencyMode ? (
          <>
            <Text style={[styles.section, styles.sectionEmergency]}>
              24/7 EMERGENCY SERVICE
            </Text>
            <Text style={styles.subhint}>
              National chains with round-the-clock dispatch. Tap a card to
              reach their dispatch page.
            </Text>
            <View style={[styles.cards, twoCol && styles.cardsTwoCol]}>
              {emergencyProviders.map((p) => (
                <ProviderCard
                  key={p.key}
                  provider={p}
                  activeTrade={activeTrade}
                  zip={zip}
                  onPress={() => openProvider(p)}
                  twoCol={twoCol}
                  highlight
                />
              ))}
            </View>
          </>
        ) : null}

        {/* YOUR PROS (Wave 49) — the pros the user has already vetted:
            saved contacts whose role/company matches the picked trade.
            Nothing beats "call the person who already knows my house." */}
        {matchingContacts.length > 0 ? (
          <>
            <Text style={styles.section}>YOUR PROS</Text>
            <Text style={styles.subhint}>
              Already in your contacts for this trade — one tap to call.
            </Text>
            <View style={[styles.cards, twoCol && styles.cardsTwoCol]}>
              {matchingContacts.map((c) => (
                <Pressable
                  key={c.id}
                  onPress={() => goTo(router, `/contact/${c.id}`)}
                  style={({ pressed }) => [
                    styles.yourProCard,
                    twoCol && styles.cardTwoCol,
                    pressed && { opacity: 0.9 },
                  ]}>
                  <View style={styles.yourProIcon}>
                    <Users size={16} color={Palette.cream} strokeWidth={2} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.yourProName} numberOfLines={1}>{c.name}</Text>
                    <Text style={styles.yourProMeta} numberOfLines={1}>
                      {[c.role, c.company].filter(Boolean).join(' · ') || 'Saved contact'}
                    </Text>
                  </View>
                  {c.phone ? (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        void Linking.openURL(`tel:${c.phone}`).catch(() => {});
                      }}
                      hitSlop={8}
                      accessibilityLabel={`Call ${c.name}`}
                      style={({ pressed }) => [styles.yourProCall, pressed && { opacity: 0.8 }]}>
                      <Phone size={14} color={Palette.forest} strokeWidth={2.2} />
                    </Pressable>
                  ) : null}
                </Pressable>
              ))}
            </View>
          </>
        ) : null}

        <Text style={styles.section}>TOP DIRECTORIES</Text>
        <Text style={styles.subhint}>
          Google Maps&apos; local pack and Yelp&apos;s reviews-first
          index — stable, ZIP-scoped deep links every time.
        </Text>
        <View style={[styles.cards, twoCol && styles.cardsTwoCol]}>
          {featured.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              activeTrade={activeTrade}
              zip={zip}
              onPress={() => openProvider(p)}
              twoCol={twoCol}
            />
          ))}
        </View>

        <Text style={styles.section}>SEARCH DIRECTORIES</Text>
        <Text style={styles.subhint}>
          Project-based pro matching and reputation databases.
        </Text>
        <View style={[styles.cards, twoCol && styles.cardsTwoCol]}>
          {searchProviders.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              activeTrade={activeTrade}
              zip={zip}
              onPress={() => openProvider(p)}
              twoCol={twoCol}
            />
          ))}
        </View>

        <Text style={styles.section}>NATIONAL CHAINS</Text>
        <Text style={styles.subhint}>
          Single point of contact, consistent pricing, available almost
          everywhere. Tap through to use their ZIP locator.
        </Text>
        <View style={[styles.cards, twoCol && styles.cardsTwoCol]}>
          {chainProviders.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              activeTrade={activeTrade}
              zip={zip}
              onPress={() => openProvider(p)}
              twoCol={twoCol}
            />
          ))}
        </View>

        <View style={styles.tipCard}>
          <View style={styles.tipIcon}>
            <Sparkles size={14} color={Palette.honeyDeep} strokeWidth={2} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tipTitle}>Save the ones you trust</Text>
            <Text style={styles.tipBody}>
              Once you&apos;ve worked with a pro you like, add them to{' '}
              <Text
                style={styles.tipLink}
                onPress={() => goTo(router, '/contacts')}>
                Contacts
              </Text>{' '}
              so they&apos;re a tap away next time something breaks.
            </Text>
          </View>
        </View>

        <View style={styles.disclaimer}>
          <Text style={styles.disclaimerText}>
            Listings come from third-party directories — Homestead
            doesn&apos;t vouch for individual companies. Verify license,
            insurance, and reviews before hiring.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function ProviderCard({
  provider,
  activeTrade,
  zip,
  onPress,
  twoCol,
  highlight,
}: {
  provider: ProProvider;
  activeTrade: string;
  zip: string;
  onPress: () => void;
  twoCol: boolean;
  highlight?: boolean;
}) {
  const supports = providerSupportsTrade(provider);
  const tone: ProviderTier = provider.tier;
  const featured = tone === 'featured';
  return (
    <Pressable
      accessibilityRole="link"
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.card,
        webTransition,
        twoCol && styles.cardTwoCol,
        featured && styles.cardFeatured,
        highlight && styles.cardHighlight,
        // Hover: tiny lift + warmer border. Featured/highlighted cards
        // already pop, so the cue stays subtle even on those tiers.
        hovered && styles.cardHover,
        pressed && { transform: [{ scale: 0.99 }] },
      ]}>
      <View
        style={[
          styles.cardIcon,
          featured && styles.cardIconFeatured,
          highlight && styles.cardIconHighlight,
        ]}>
        {featured ? (
          provider.key === 'google_local_services' ? (
            <ShieldCheck size={20} color={Palette.cream} strokeWidth={1.8} />
          ) : (
            <SearchIcon size={20} color={Palette.cream} strokeWidth={1.8} />
          )
        ) : highlight ? (
          <Siren size={20} color={Palette.cream} strokeWidth={1.9} />
        ) : tone === 'chain' ? (
          <Building2 size={20} color={Palette.forest} strokeWidth={1.7} />
        ) : (
          <SearchIcon size={20} color={Palette.forest} strokeWidth={1.7} />
        )}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={[
            styles.cardName,
            featured && styles.cardNameFeatured,
            highlight && styles.cardNameHighlight,
          ]}
          numberOfLines={1}>
          {provider.name}
        </Text>
        <Text
          style={[
            styles.cardTagline,
            featured && styles.cardTaglineFeatured,
            highlight && styles.cardTaglineHighlight,
          ]}
          numberOfLines={2}>
          {provider.tagline}
        </Text>
        <Text
          style={[
            styles.cardQuery,
            featured && styles.cardQueryFeatured,
            highlight && styles.cardQueryHighlight,
          ]}
          numberOfLines={1}>
          {provider.isLandingOnly
            ? 'Open the directory'
            : supports
            ? `Search "${activeTrade.trim() || EMPTY_TRADE_FALLBACK}"${zip ? ` near ${zip}` : ''}`
            : 'Open the directory'}
        </Text>
      </View>
      <ExternalLink
        size={16}
        color={featured || highlight ? Palette.cream : Palette.inkFaint}
        strokeWidth={1.7}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  topbarLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  shortcutBtn: {
    width: 32, height: 32, borderRadius: 16, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: {
    fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink, letterSpacing: -0.3,
  },
  lead: {
    marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },

  searchCard: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  searchInput: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
    color: Palette.ink,
    paddingVertical: 6,
  },
  zipBox: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingLeft: 10,
    borderLeftWidth: 1, borderLeftColor: Palette.line,
  },
  zipBoxLabel: {
    fontFamily: Fonts.sansBold, fontSize: 11,
    color: Palette.inkFaint, letterSpacing: 0.6,
  },
  zipInput: {
    fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink,
    minWidth: 56, textAlign: 'center', paddingVertical: 4,
  },

  queryBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 9,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    paddingHorizontal: 14, paddingVertical: 11,
    backgroundColor: Palette.forest,
    borderRadius: 14,
  },
  queryBannerEmergency: {
    backgroundColor: Palette.clay,
  },
  queryBannerText: {
    color: Palette.cream, fontFamily: Fonts.sansSemibold, fontSize: 13,
    flex: 1,
  },
  queryBannerEm: { fontFamily: Fonts.sansBold, color: Palette.cream },
  queryBannerSub: { color: 'rgba(251,246,234,0.85)' },

  tabsRow: {
    paddingHorizontal: Spacing.four, marginTop: Spacing.three,
    paddingBottom: 2, gap: 6,
  },
  tab: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 14, paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card,
    marginRight: 6,
  },
  tabSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  tabSelEmergency: { backgroundColor: Palette.clay, borderColor: Palette.clay },
  tabText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  tabTextSel: { color: Palette.cream },

  tradeGrid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6,
    paddingHorizontal: Spacing.four, marginTop: 10,
  },
  tradeChip: {
    paddingHorizontal: 11, paddingVertical: 6, borderRadius: 999,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
  },
  tradeChipSel: { backgroundColor: '#EEF3EE', borderColor: Palette.forest },
  tradeChipText: { fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.ink },
  tradeChipTextSel: { color: Palette.forest, fontFamily: Fonts.sansBold },

  section: {
    marginTop: Spacing.five, marginHorizontal: Spacing.four,
    fontFamily: Fonts.sansBold, fontSize: 12,
    color: Palette.inkFaint, letterSpacing: 0.7, marginBottom: 6,
  },
  sectionEmergency: { color: Palette.clay, letterSpacing: 1 },
  subhint: {
    marginHorizontal: Spacing.four, marginBottom: 10,
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, lineHeight: 17,
  },

  cards: { paddingHorizontal: Spacing.four, gap: 8 },
  // Desktop two-column layout. `gap: 8` on the wrap covers the
  // between-card spacing; each card claims 48% so two fit comfortably.
  cardsTwoCol: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  card: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 13,
  },
  yourProCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#EEF3EE',
    borderWidth: 1.5,
    borderColor: Palette.forest,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  yourProIcon: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  yourProName: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  yourProMeta: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  yourProCall: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  cardTwoCol: {
    width: '48.5%',
  },
  cardFeatured: { backgroundColor: Palette.forest, borderColor: Palette.forestLight },
  cardHighlight: {
    backgroundColor: Palette.clay,
    borderColor: 'rgba(176,95,60,0.7)',
  },
  // Hover state: 1px translateY lift + forest border tint. The lift
  // is intentionally small — Find-a-Pro is a list, not a feature,
  // so the cue should be felt more than seen.
  cardHover: {
    borderColor: Palette.forestLight,
    transform: [{ translateY: -1 }],
  },
  cardIcon: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  cardIconFeatured: { backgroundColor: Palette.forestLight },
  cardIconHighlight: { backgroundColor: 'rgba(255,255,255,0.16)' },
  cardName: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  cardNameFeatured: { color: Palette.cream },
  cardNameHighlight: { color: Palette.cream },
  cardTagline: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft, marginTop: 1,
  },
  cardTaglineFeatured: { color: 'rgba(251,246,234,0.85)' },
  cardTaglineHighlight: { color: 'rgba(251,246,234,0.85)' },
  cardQuery: {
    fontFamily: Fonts.sansSemibold, fontSize: 12.5,
    color: Palette.forest, marginTop: 3,
  },
  cardQueryFeatured: { color: Palette.honeySoft },
  cardQueryHighlight: { color: Palette.honeySoft },

  tipCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 11,
    marginTop: Spacing.five,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    backgroundColor: Palette.honeySoft,
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.45)',
  },
  tipIcon: {
    width: 26, height: 26, borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.55)',
    alignItems: 'center', justifyContent: 'center',
  },
  tipTitle: { fontFamily: Fonts.sansBold, fontSize: 13, color: '#3A2A06' },
  tipBody: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: '#5A4012', lineHeight: 17,
  },
  tipLink: {
    fontFamily: Fonts.sansBold,
    color: Palette.honeyDeep,
    textDecorationLine: 'underline',
  },

  disclaimer: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    padding: 13, borderRadius: 12,
    backgroundColor: 'rgba(167,156,136,0.16)',
    borderWidth: 1, borderColor: 'rgba(167,156,136,0.35)',
  },
  disclaimerText: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft, lineHeight: 17,
  },
});
